/*       */

import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withStyles } from '@material-ui/core/styles';
import { uploadImage, loadEntity } from 'store/actions/entities/entitiesActions';
import { get } from 'app/utils/lo/lo';
import PageNotAllowed from 'app/containers/ErrorPages/PageNotAllowed';
import { bind, memoize } from 'app/utils/decorators/decoratorUtils';
import { loadOrganisationSettings, showToastr, refreshOrganisationImage, 
    updateOrganisationSettings, setDocumentTitle, refreshCompanyLogoLarge } from 'store/actions/app/appActions';
import Container from 'app/components/atoms/Container/Container';
import ContentArea from 'app/components/molecules/PageContent/ContentArea';
import FormGenerator from 'app/containers/Designer/Form/components/FormGenerator';
import { Button, CircularProgress, Typography } from '@mic3/platform-ui';
import HeaderBar from 'app/components/molecules/HeaderBar/HeaderBar';
import PageTemplate from 'app/components/templates/PageTemplate';
import { getOnlyUpdatedData } from 'app/utils/app/appUtils';
import { organistationEntityId, settingsEntityType } from 'app/config/config';
import Loader from 'app/components/atoms/Loader/Loader';
import { getPermissions } from 'app/config/rolesConfig';
import { modulesAndPageTitles } from 'app/config/typesConfig';
import { shallowEquals } from 'app/utils/utils';


const useStyles = withStyles({
    label: {
        margin: '0 0 8px 0 !important',
        fontWeight: '600 !important',
        fontSize: '12px !important',
        lineHeight: '16px !important',
        letterSpacing: '0.4px !important',
        opacity: '0.7',
    },
});

class OrganisationSettings extends PureComponent {
    static propTypes = {
        profile: PropTypes.object,
        updateOrganisationSettings: PropTypes.func.isRequired,
        uploadImage: PropTypes.func.isRequired,
        loadOrganisationSettings: PropTypes.func.isRequired,
    };

    formRef = React.createRef();

    constructor(props) {
        super(props);
        props.loadOrganisationSettings();
        props.loadEntity(settingsEntityType, organistationEntityId);
    }

    componentDidMount() {
        this.props.setDocumentTitle(modulesAndPageTitles.adminConsole.site.general);
    }


    componentDidUpdate(prevProps) {
        if (!shallowEquals(prevProps.details, this.props.details, ['primary.system_setting/image.[0].uploadedDate'])) {
            this.props.refreshCompanyLogoLarge();
        }
    }

    @bind
    @memoize()
    fieldDefinitions(organisation, isAdmin) {
        const { name, image } = organisation;
        return [
            {
                field: 'panel',
                type: 'panel',
                properties: {
                    header: 'Organisation Details',
                    expanded: true,
                },
                children: [
                  
                    {
                        field: 'name',
                        type: 'text',
                        properties: {
                            label: 'Name',
                            name: 'name',
                        },
                        constraints: {
                            required: true,
                            maxLength: 60,
                            minLength: 3,
                        },
                    },
                    {
                        field: 'fullName',
                        type: 'text',
                        properties: {
                            label: 'Organisation Full Name',
                            name: 'fullName',
                        },
                        constraints: { minLength: 1 },
                    },
                    {
                        field: 'contact',
                        type: 'personTypeahead',
                        properties: {
                            label: 'Contact Person',
                            name: 'contact',
                            filterBy: isAdmin ? null : [{ field: 'active', op: '=', value: true }],
                        },
                    },
                    {type: 'divider'},
                    {
                        field: '',
                        type: 'avatarEditor',
                        properties: {
                            name: 'image',
                            label: 'Company logo square (40 x 40 minimum)',
                            initials: name,
                            image,
                            AvatarGridProps: {
                                direction: 'column-reverse',
                                justify: 'space-around',
                                alignItems: 'start',
                            },
                            AvatarProps: {
                                style: { marginTop: '10px' }
                            },
                            classes: {
                                label: this.props.classes.label
                            }
                           
                        },
                    },
                    {
                        type: 'header',
                        properties: {
                            text: 'Click image to replace',
                            variant: 'caption',
                            style: { opacity: 0.38 }
                        },
                    },
                    {type: 'divider'},
                    {
                        field: 'companyLogoLarge',
                        type: 'file',
                        properties: {
                            entityType: settingsEntityType,
                            entityId: organistationEntityId,
                            name: 'system_setting/image',
                            isPrimary: true,
                            isFullImage: true,
                            label: 'Company logo horizontal (320 x 160 minimum)',
                            acceptedExtensions: ['.jpg', '.jpeg', '.png'],
                        }
                    },
                    {type: 'divider'},
                    {
                        field: 'companyLoginLogo',
                        type: 'file',
                        properties: {
                            entityType: settingsEntityType,
                            entityId: organistationEntityId,
                            name: 'system_setting/file',
                            isPrimary: true,
                            isFullImage: true,
                            label: 'Login logo horizontal (320 x 160 minimum)',
                            acceptedExtensions: ['.jpg', '.jpeg', '.png'],
                        }
                    },
                    {type: 'divider'},
                    {
                        field: 'companyLoginBackgrounds',
                        type: 'file',
                        properties: {
                            entityType: settingsEntityType,
                            entityId: organistationEntityId,
                            name: 'system_setting/attachments',
                            isPrimary: true,
                            isFullImage: true,
                            label: 'Login backgrounds, maximum 10 images (1400 x 1020 recommended)',
                            multiple: true,
                            reverseList: true,
                            maxFiles: 10,
                            acceptedExtensions: ['.jpg', '.jpeg', '.png'],
                            // moveOptions: true,
                        }
                    },
                ],
            },

            {
                field: 'panel',
                type: 'panel',
                properties: {
                    header: 'Superset Configuration',
                    expanded: true,
                },
                children: [
                    {
                        type: 'text',
                        properties: {
                            label: 'URL',
                            name: 'reportingUrl',
                        },
                    },
                    {
                        type: 'text',
                        properties: {
                            label: 'Superset Token',
                            name: 'reportingToken',
                        },
                    },
                ],
            },
            {
                field: 'panel',
                type: 'panel',
                properties: {
                    header: 'Learning Platform Configuration',
                    expanded: true,
                },
                children: [
                    {
                        type: 'text',
                        properties: {
                            label: 'URL',
                            name: 'learningUrl',
                        },
                    },
                ],
            },
        ];
    }

    @bind
    uploadImage(image) {
        this.props.uploadImage(organistationEntityId, settingsEntityType, image).then(resp => {
            if (!(resp instanceof Error)) {
                this.props.refreshOrganisationImage();
            }
        });
    }

    @bind
    handleChange(data, { name, value }) {
        if (name === 'image') {
            return this.uploadImage(value);
        }
    }

    @bind
    onFormSubmit(event) {
        event.preventDefault();
        this.formRef.current.isValidForm().then(({ data: formData, errors }) => {
            if (!errors) {
                const { organisation } = this.props;
                const { name, fullName, contact, learningUrl, reportingUrl, reportingToken } = formData || {};
                const entity = getOnlyUpdatedData(organisation, { name, fullName, contact, learningUrl, reportingUrl, reportingToken });
                entity && Object.keys(entity)?.length && this.props.updateOrganisationSettings(entity);
            }
        });
    }

    @bind
    renderSaveButton(canEdit) {
        const { isLoading } = this.props;
        if (!canEdit) return null;
        return isLoading ? (
            <CircularProgress key={113} size={24} color="primary" />
        ) : (
            <Button key={113} onClick={this.onFormSubmit} color="primary" form="form" type="submit">
                Save
            </Button>
        );
    }

    render() {
        const { organisation, configurations, profile, organisationImage, isLoading, 
            details } = this.props;
        const { isAdmin } = profile || {};
        const permissionsSet = new Set(this.props.permissions);
        const canSeeSettings = isAdmin || permissionsSet.has('admin.settings') || permissionsSet.has('admin.site');
        const siteEntityPermissions = getPermissions(organisation?.role);
        const companyLogoLarge = get(details, `primary.system_setting/image`);
        const companyLoginLogo = get (details, `primary.system_setting/file`);
        const companyLoginBackgrounds = get (details,`primary.system_setting/attachments`)
        const data = { ...organisation, config: configurations, image: organisationImage, 
            'system_setting/image': companyLogoLarge, 'system_setting/file': companyLoginLogo,
            'system_setting/attachments': companyLoginBackgrounds
        };
        const canEdit = siteEntityPermissions?.canEdit;
        if (!canSeeSettings || !organisation) {
            return <PageNotAllowed title="Organisation Settings" />;
        }
        return (
            <PageTemplate title={organisation.name} overflowHidden>
                <HeaderBar rowDirection right={[this.renderSaveButton(canEdit)]} left={<Typography variant="h6">General</Typography>} />
                <ContentArea withHeader>
                    {isLoading && <Loader absolute backdrop />}
                    <Container width="1024">
                        <FormGenerator
                            components={this.fieldDefinitions(data, isAdmin)}
                            data={data}
                            ref={this.formRef}
                            disabled={!canEdit}
                            onChange={this.handleChange}
                        />
                    </Container>
                </ContentArea>
            </PageTemplate>
        );
    }
}

export default connect(
    state => ({
        profile: state.user.profile,
        id: get(state.app, 'organisation.id', ''),
        type: 'organisation',
        isLoading: state.app.organisationSettings.isLoading,
        organisation: get(state.app, 'organisationSettings.data', {}),
        organisationImage: state.app.organisationImage,
        permissions: state.user.profile.permissions,
        configurations: get(state.app, 'configurations', {}),
        details: get(state.entities.sidebar, 'details.data', {}),
    }),
    {
        loadOrganisationSettings,
        uploadImage,
        showToastr,
        refreshOrganisationImage,
        refreshCompanyLogoLarge,
        updateOrganisationSettings,
        setDocumentTitle,
        loadEntity
    }
)(useStyles(OrganisationSettings)); // eslint-disable-line
